import { useCallback } from 'react';

import {
  useSelect,
  UseSelectState,
  UseSelectStateChange,
  UseSelectStateChangeOptions,
  GetPropsCommonOptions,
  UseComboboxGetToggleButtonPropsOptions,
  useMultipleSelection,
  UseMultipleSelectionGetDropdownProps,
  UseMultipleSelectionProps,
  UseSelectGetItemPropsOptions,
} from 'downshift';

import { useDropdown } from '.';
import {
  checkReferenceEquality,
  defaultIsItemDisabled,
  defaultLabelGetter,
  wrapGetSelectedItemProps,
} from '../helpers';
import {
  DefaultItem,
  GenericItem,
  ItemsOrGroupedItems,
  UseMultiDropdownProps,
} from '../types';

interface DownshiftFocusEvent extends FocusEvent {
  preventDownshiftDefault?: boolean;
}
/**
 * A hook used to manage all logic needed for a multiple select Dropdown.
 */
const useMultiDropdown = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLDivElement | HTMLLabelElement,
>({
  items,
  groupedItems,
  selectedItems: selectedItemsProp,
  itemToString = (item) => defaultLabelGetter(item as DefaultItem | null),
  isItemDisabled = defaultIsItemDisabled,
  ...props
}: UseMultiDropdownProps<TItem, TTarget>) => {
  const maybeGroupedItems = items || groupedItems || [];

  checkReferenceEquality({
    ...props,
    items: maybeGroupedItems,
    selectedItems: selectedItemsProp,
  });

  const useMultipleSelectionArgs: UseMultipleSelectionProps<TItem> = {};
  if (selectedItemsProp) {
    // Setting this to undefined results in "cannot get length of undefined", so do this hack
    useMultipleSelectionArgs.selectedItems = selectedItemsProp;
  }

  const {
    getDropdownProps,
    getSelectedItemProps: rawGetSelectedItemProps,
    ...useMultipleSelectionReturnValue
  } = useMultipleSelection<TItem>({
    ...useMultipleSelectionArgs,
    // Setting initial active index blocks downshift from focusing input on mount
    initialActiveIndex: 0,
    ...props,
  });

  const stateReducer = (
    state: UseSelectState<TItem>,
    { changes, type }: UseSelectStateChangeOptions<TItem>,
  ): Partial<UseSelectState<TItem>> => {
    switch (type) {
      case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
      case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
      case useSelect.stateChangeTypes.ItemClick:
        return {
          ...changes,
          isOpen: true, // keep menu open after selection
          highlightedIndex: state.highlightedIndex, // maintain highlighted item after selection
        };
      default:
        return changes;
    }
  };

  const onStateChange = ({
    type,
    selectedItem,
  }: UseSelectStateChange<TItem>) => {
    switch (type) {
      case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
      case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
      case useSelect.stateChangeTypes.ItemClick:
        if (!selectedItem) {
          break;
        }

        if (
          !useMultipleSelectionReturnValue.selectedItems.includes(selectedItem)
        ) {
          useMultipleSelectionReturnValue.addSelectedItem(selectedItem);
        } else {
          useMultipleSelectionReturnValue.removeSelectedItem(selectedItem);
        }
        break;
      default:
        break;
    }
  };

  // workaround, the implicit XOR type gets lost causing type errors.  Casting it here makes it explicit
  const itemArgs = { items, groupedItems } as ItemsOrGroupedItems<TItem>;

  const {
    getToggleButtonProps: rawGetToggleButtonProps,
    isOpen,
    getItemProps: rawGetItemProps,
    controlProps: allControlProps,
    menuProps: allMenuProps,
    ...useDropdownReturnValue
  } = useDropdown({
    ...itemArgs,
    selectedItem: null,
    itemToString,
    stateReducer,
    onStateChange,
    isItemDisabled,
    ...props,
  });

  const { selectedItem: _ignored1, ...controlProps } = allControlProps;
  const {
    selectedItem: _ignored2,
    getItemProps: _ignoredGetItemProps,
    ...menuProps
  } = allMenuProps;

  const getToggleButtonProps = (
    options?: UseComboboxGetToggleButtonPropsOptions &
      UseMultipleSelectionGetDropdownProps,
    extraOptions?: GetPropsCommonOptions,
  ) =>
    rawGetToggleButtonProps({
      // downshift useSelect is for some reason sending a toggle button blur
      // event after the click event, when you click on the toggle button the
      // very first time a page loads found this somewhat related issue that seems to fix this:
      // https://github.com/downshift-js/downshift/issues/966
      onBlur: (event: DownshiftFocusEvent) => {
        event.preventDownshiftDefault = true;
      },
      ...getDropdownProps(
        { preventKeyAction: isOpen, ...options },
        extraOptions,
      ),
    });

  const getItemProps = useCallback(
    ({
      item,
      isSelected: isSelectedProp,
      ...itemProps
    }: UseSelectGetItemPropsOptions<TItem>) => {
      const isSelected =
        isSelectedProp ||
        useMultipleSelectionReturnValue.selectedItems.includes(item);

      const toReturn = rawGetItemProps({
        item,
        isSelected,
        ...itemProps,
      });

      return { ...toReturn, isMultiple: true };
    },
    [rawGetItemProps, useMultipleSelectionReturnValue.selectedItems],
  );

  const getSelectedItemProps: typeof rawGetSelectedItemProps = useCallback(
    (args) =>
      wrapGetSelectedItemProps({
        getSelectedItemProps: rawGetSelectedItemProps,
        isItemDisabled,
      })(args),
    [isItemDisabled, rawGetSelectedItemProps],
  );

  return {
    /**
     * @deprecated use value returned from controlProps or menuProps
     */
    isOpen,
    /**
     * @deprecated use value returned from menuProps
     */
    getItemProps,
    /**
     * @deprecated use value returned from controlProps
     */
    getToggleButtonProps,
    getSelectedItemProps,
    controlProps: {
      ...controlProps,
      getToggleButtonProps,
      selectedItems: useMultipleSelectionReturnValue.selectedItems,
      getSelectedItemProps,
      removeSelectedItem: useMultipleSelectionReturnValue.removeSelectedItem,
    },
    menuProps: {
      ...menuProps,
      selectedItems: useMultipleSelectionReturnValue.selectedItems,
      addSelectedItem: useMultipleSelectionReturnValue.addSelectedItem,
      removeSelectedItem: useMultipleSelectionReturnValue.removeSelectedItem,
      getItemProps,
    },
    ...useMultipleSelectionReturnValue,
    ...useDropdownReturnValue,
  };
};

export default Object.assign(useMultiDropdown, {
  stateChangeTypes: useDropdown.stateChangeTypes,
  multipleSelectionStateChangeTypes: useMultipleSelection.stateChangeTypes,
});
